import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react'
import { Check, CircleAlert, X } from 'lucide-react'
import { cn } from '@/lib/utils'

type ToastKind = 'success' | 'error' | 'info'

interface ToastItem {
  id: number
  kind: ToastKind
  text: string
}

interface ToastApi {
  success: (text: string) => void
  error: (text: string) => void
  info: (text: string) => void
}

const ToastContext = createContext<ToastApi | null>(null)

/** How long a toast stays. Failures also show inline where the action was. */
const TOAST_MS = 4000

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const next = useRef(1)

  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), [])
  const push = useCallback(
    (kind: ToastKind, text: string) => {
      const id = next.current++
      setItems((xs) => [...xs.slice(-2), { id, kind, text }])
      window.setTimeout(() => dismiss(id), TOAST_MS)
    },
    [dismiss],
  )
  const api = useMemo<ToastApi>(
    () => ({
      success: (t) => push('success', t),
      error: (t) => push('error', t),
      info: (t) => push('info', t),
    }),
    [push],
  )

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-3 bottom-[calc(72px+env(safe-area-inset-bottom))] z-[60] flex flex-col items-center gap-2 md:inset-x-auto md:bottom-5 md:right-5 md:items-end"
      >
        {items.map((t) => (
          <div
            key={t.id}
            role={t.kind === 'error' ? 'alert' : 'status'}
            className={cn(
              'pointer-events-auto flex w-full max-w-sm animate-toast-in items-start gap-2 rounded-lg border bg-card px-3 py-2.5 text-body shadow-overlay',
              t.kind === 'error' ? 'border-status-failed/40' : 'border-border',
            )}
          >
            {t.kind === 'error' ? (
              <CircleAlert size={16} className="mt-0.5 shrink-0 text-status-failed" aria-hidden />
            ) : (
              <Check size={16} className="mt-0.5 shrink-0 text-status-published" aria-hidden />
            )}
            <span className="min-w-0 flex-1">{t.text}</span>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              className="-mr-1 rounded p-0.5 text-muted-foreground hover:text-foreground"
              aria-label="알림 닫기"
            >
              <X size={14} aria-hidden />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

/** `toast.success('예약했다')`, `toast.error(message)`. Outside the provider
 *  (a test rendering one component) the calls do nothing. */
export function useToast(): ToastApi {
  return useContext(ToastContext) ?? NOOP
}

const NOOP: ToastApi = { success: () => {}, error: () => {}, info: () => {} }
